import { FC, useCallback, useMemo } from 'react'
import {
  Button,
  Icon,
  Popover,
  Portal,
  Text,
  Transition,
  useMediaQuery,
} from '@lounge-fe/ui-kit'
import { useTranslation } from 'react-i18next'
import { useCart } from '@/features/cart/hooks'
import { useRecentlyAddedProducts } from '@/features/cart/hooks/useRecentlyAddedProducts'
import { useBasket } from './hooks/useBasket'

export const ItemAddedPopover: FC = () => {
  const isXLDesktop = useMediaQuery(
    ({ screens }) => `(min-width: ${screens['2xl']})`,
  )

  const { recentlyAddedProductIds, clearRecentlyAddedProductIds } =
    useRecentlyAddedProducts()

  const { openBasket, isOpen: isBasketOpen } = useBasket()

  const show = useMemo(
    () => recentlyAddedProductIds.length > 0 && !isBasketOpen,
    [recentlyAddedProductIds.length, isBasketOpen],
  )

  const { t } = useTranslation()

  const { data: { itemCount = 0 } = {} } = useCart()

  const onBasketOpen = useCallback(() => {
    clearRecentlyAddedProductIds()
    openBasket()
  }, [])

  const onClose = useCallback(() => {
    clearRecentlyAddedProductIds()
  }, [])

  if (isXLDesktop) return

  return (
    <Popover isOpen={show}>
      <Portal>
        <Popover.Backdrop className="z-[60]" />
        <Transition.Slide
          direction="bottom"
          className="fixed bottom-6 left-0 right-0 z-[70] mx-6"
          data-testid="basket-mobile-transition"
        >
          <Popover.Content
            className="bg-white flex flex-col p-5 gap-y-3 "
            onClickAway={onClose}
          >
            <div className="flex gap-x-2">
              <Icon.CheckOutline />
              <Text variant="title-five">{t('cart.items_added')}</Text>
            </div>

            <div className="flex flex-wrap gap-y-2 gap-x-5">
              <Button
                className="gap-x-1 grow min-w-[calc(50%-theme(spacing.5))]"
                variant="outline"
                onClick={onBasketOpen}
              >
                {t('cart.bag')}
                {itemCount > 0 && <span>{`(${itemCount})`}</span>}
              </Button>
              <Button
                className="gap-x-1 grow min-w-[calc(50%-theme(spacing.5))]"
                variant="solid"
                as="a"
                href="/checkout"
              >
                <Icon.Lock width={16} height={16} />
                {t('cart.checkout')}
              </Button>
            </div>
          </Popover.Content>
        </Transition.Slide>
      </Portal>
    </Popover>
  )
}
